// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";

// CSS
import "./Devices.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import DevicesEditAddDialog from "./DevicesEditAddDialog/DevicesEditAddDialog";
import DevicesPreviousUsers from "./DevicesPreviousUsers/DevicesPreviousUsers";
import DevicesServiceHistoryDialog from "./DevicesServiceHistoryDialog/DevicesServiceHistoryDialog";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import {
	checkboxSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const Devices = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();
	const [searchParams] = useSearchParams();
	const editDeviceId = searchParams.get("editDeviceId");

	// STATES
	const [devices, setDevices] = useState([]);
	const [deviceRows, setDeviceRows] = useState([]);
	const [deviceColumns, setDeviceColumns] = useState([]);

	const [employees, setEmployees] = useState([]);
	const [offices, setOffices] = useState([]);
	const [deviceStatuses, setDeviceStatuses] = useState([]);
	const [devicesToEmployees, setDevicesToEmployees] = useState([]);
	const [serviceHistories, setServiceHistories] = useState([]);
	const [incidentTypes, setIncidentTypes] = useState([]);
	const [purchases, setPurchases] = useState([]);
	const [leases, setLeases] = useState([]);
	const [devicesToLeases, setDevicesToLeases] = useState([]);
	const [deviceCategories, setDeviceCategories] = useState([]);

	const [recordToEdit, setRecordToEdit] = useState({});
	const [loading, setLoading] = useState(false);
	const [editAddDialogOpen, setEditAddDialogOpen] = useState(false);
	const [editing, setEditing] = useState(false);
	const [autoEditHandled, setAutoEditHandled] = useState(false);
	const [previousUsersOpen, setPreviousUsersOpen] = useState(false);
	const [recordToShowPreviousUsers, setRecordToShowPreviousUsers] = useState(
		{}
	);
	const [serviceHistoryOpen, setServiceHistoryOpen] = useState(false);
	const [recordToShowServiceHistory, setRecordToShowServiceHistory] =
		useState({});

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Devices",
			employeeID: employeeID,
			notes: "Accessed the Devices page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		handleAPICalls();
	}, []);

	useEffect(() => {
		if (
			devices.length > 0 &&
			deviceStatuses.length > 0 &&
			offices.length > 0
		) {
			createColumnData();
			createRowData();
		}
	}, [devices, deviceStatuses, offices]);

	useEffect(() => {
		if (
			autoEditHandled ||
			!editDeviceId ||
			devices.length === 0 ||
			deviceCategories.length === 0 ||
			deviceStatuses.length === 0 ||
			offices.length === 0 ||
			employees.length === 0 ||
			purchases.length === 0 ||
			leases.length === 0 ||
			devicesToLeases.length === 0 ||
			devicesToEmployees.length === 0
		) {
			return;
		}

		const deviceToEdit = devices.find(
			(device) => device.id === Number(editDeviceId)
		);

		if (deviceToEdit) {
			handleEditClick(deviceToEdit);
		}

		setAutoEditHandled(true);
	}, [
		autoEditHandled,
		editDeviceId,
		devices,
		deviceCategories,
		deviceStatuses,
		offices,
		employees,
		purchases,
		leases,
		devicesToLeases,
		devicesToEmployees,
	]);

	// INPUT HANDLERS
	const handleAddClick = () => {
		setEditing(false);
		setEditAddDialogOpen(true);
	};

	const handleEditClick = (record) => {
		setRecordToEdit(record);
		setEditing(true);
		setEditAddDialogOpen(true);
	};

	const handleEditAddDialogClose = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
		handleAPICalls();
	};

	const handleEditAddDialogCloseNoRefresh = () => {
		setEditing(false);
		setEditAddDialogOpen(false);
		setRecordToEdit({});
	};

	const handlePreviousUsersClick = (record) => {
		setRecordToShowPreviousUsers(record);
		setPreviousUsersOpen(true);
	};

	const handlePreviousUsersClose = () => {
		setPreviousUsersOpen(false);
		setRecordToShowPreviousUsers({});
	};

	const handleServiceHistoryClick = (record) => {
		setRecordToShowServiceHistory(record);
		setServiceHistoryOpen(true);
	};

	const handleServiceHistoryClose = () => {
		setServiceHistoryOpen(false);
		setRecordToShowServiceHistory({});
	};

	// API FUNCTIONS

	const handleAPICalls = async () => {
		setLoading(true);
		await getDevices();
		await getEmployees();
		await getOffices();
		await getDeviceStatuses();
		await getDevicesToEmployees();
		await getServiceHistories();
		await getIncidentTypes();
		await getPurchases();
		await getLeases();
		await getDevicesToLeases();
		await getDeviceCategories();
		setLoading(false);
	};

	const getDevices = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/all`
			);
			setDevices(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getEmployees = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/employees/all`
			);
			setEmployees(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getOffices = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/employees/offices/all`
			);
			setOffices(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getDeviceStatuses = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/deviceStatuses/all`
			);
			setDeviceStatuses(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getDevicesToEmployees = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/devicesToEmployees/all`
			);
			setDevicesToEmployees(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getServiceHistories = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/serviceHistories/all`
			);
			setServiceHistories(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getIncidentTypes = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/incidentTypes/all`
			);
			setIncidentTypes(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getPurchases = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/purchases/all`
			);
			setPurchases(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getLeases = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/leases/all`
			);
			setLeases(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getDevicesToLeases = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/devicesToLeases/all`
			);
			setDevicesToLeases(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	const getDeviceCategories = async () => {
		try {
			let res = await getAPICall(
				instance,
				accounts[0],
				`/api/hardwareInventory/deviceCategories/all`
			);
			setDeviceCategories(res.data);
		} catch (error) {
			console.error(error);
		}
	};

	// HELPER FUNCTIONS

	const createColumnData = () => {
		setDeviceColumns([
			{
				accessorKey: "j2_device_name",
				header: () => <span>J2 Name</span>,
				label: "J2 Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "default_name",
				header: () => <span>Name</span>,
				label: "Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "status_display_name",
				header: () => <span>Status</span>,
				label: "Status",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "checkbox",
				filterOptions: deviceStatuses.map((status) => ({
					id: status.id,
					label: status.display_name,
				})),
				filterFn: checkboxSort,
			},
			{
				accessorKey: "office_state",
				header: () => <span>Office</span>,
				label: "Office",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "checkbox",
				filterOptions: offices.map((office) => ({
					id: office.id,
					label: office.state,
				})),
				filterFn: checkboxSort,
			},
			{
				accessorKey: "model",
				header: () => <span>Model</span>,
				label: "Model",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "ram",
				header: () => <span>RAM</span>,
				label: "RAM",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "storage",
				header: () => <span>Storage</span>,
				label: "Storage",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "os",
				header: () => <span>OS</span>,
				label: "OS",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "serial_number",
				header: () => <span>Serial # / Service Tag</span>,
				label: "Serial # / Service Tag",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "manufacturer",
				header: () => <span>Manufacturer</span>,
				label: "Manufacturer",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "employee_full_name",
				header: () => <span>Current User</span>,
				label: "Current User",
				cell: ({ row }) =>
					row.original.employee_id ? (
						<span>{row.original.employee_full_name}</span>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "previous_users_edit_service_history",
				header: "",
				cell: (props) => (
					<Box display="flex" justifyContent="end" gap={1}>
						<Tooltip title="Previous Users">
							<IconButton
								onClick={() =>
									handlePreviousUsersClick(
										props.row.original.device
									)
								}
								disableRipple={true}
								sx={{
									minHeight: 0,
									minWidth: 0,
									padding: 0,
								}}
							>
								<RecentActorsIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Edit Device">
							<IconButton
								onClick={() =>
									handleEditClick(props.row.original.device)
								}
								disableRipple={true}
								sx={{
									minHeight: 0,
									minWidth: 0,
									padding: 0,
								}}
							>
								<EditIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Service History">
							<IconButton
								onClick={() =>
									handleServiceHistoryClick(
										props.row.original.device
									)
								}
								disableRipple={true}
								sx={{
									minHeight: 0,
									minWidth: 0,
									padding: 0,
								}}
							>
								<HistoryIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				),
				enableSorting: false,
				enableColumnFilter: false,
			},
		]);
	};

	const createRowData = () => {
		setDeviceRows(
			devices.map((device) => ({
				j2_device_name: device.j2_device_name,
				default_name: device.default_name,
				status_display_name: device.status_display_name,
				office_state: device.office_state,
				model: device.model,
				ram: device.ram,
				storage: device.storage,
				os: device.os,
				serial_number: device.serial_number,
				manufacturer: device.manufacturer,
				employee_id: device.employee_id,
				employee_full_name: device.employee_id
					? device.employee_first_name +
					  " " +
					  device.employee_last_name
					: null,
				device: device, // Keep the full device object for reference
			}))
		);
	};

	// RENDER
	return (
		<div className="Devices">
			<div className="DevicesHeader">
				<div className="DevicesHeaderTitle">
					Devices
					<Button
						variant="contained"
						color="primary"
						onClick={handleAddClick}
						disabled={loading}
					>
						New Device Record
					</Button>
				</div>
			</div>

			{loading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!loading && (
				<div className="tableContainerDiv">
					{devices.length === 0 && <J2NoResultsFound />}
					{devices.length > 0 &&
						deviceColumns.length > 0 &&
						deviceRows.length > 0 && (
							<TanStackTable
								columnData={deviceColumns}
								rowData={deviceRows}
							/>
						)}
				</div>
			)}
			<DevicesEditAddDialog
				open={editAddDialogOpen}
				onClose={handleEditAddDialogClose}
				onCloseNoRefresh={handleEditAddDialogCloseNoRefresh}
				recordToEdit={recordToEdit}
				employees={employees}
				offices={offices}
				deviceStatuses={deviceStatuses}
				devicesToEmployees={devicesToEmployees}
				editing={editing}
				purchases={purchases}
				leases={leases}
				devicesToLeases={devicesToLeases}
				deviceCategories={deviceCategories}
				devices={devices}
			/>
			<DevicesPreviousUsers
				open={previousUsersOpen}
				onClose={handlePreviousUsersClose}
				recordToShowPreviousUsers={recordToShowPreviousUsers}
				devicesToEmployees={devicesToEmployees}
			/>
			<DevicesServiceHistoryDialog
				open={serviceHistoryOpen}
				onClose={handleServiceHistoryClose}
				recordToShowServiceHistory={recordToShowServiceHistory}
				serviceHistories={serviceHistories}
				incidentTypes={incidentTypes}
				getServiceHistories={getServiceHistories}
			/>
		</div>
	);
};

// EXPORT
export default Devices;
